import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { 
  getUserTagPreferences, 
  updateUserTagPreference, 
  deleteUserTagPreference,
  getUserToolPreferences,
  updateUserToolPreference,
  deleteUserToolPreference,
  TagPreferenceType,
  ToolPreferenceType
} from '@/lib/supabase'

export interface Preferences {
  included: string[]
  excluded: string[]
}

type PreferenceType<T> = T extends 'tag' ? TagPreferenceType : ToolPreferenceType;
type ItemName<T> = T extends 'tag' ? 'tagName' : 'toolName';

// Generic hook to handle both tag and tool preferences
export function usePreferences<T extends 'tag' | 'tool'>(type: T, userId: string) {
  const queryClient = useQueryClient()
  const queryKey = [`${type}Preferences`, userId]
  
  // Select the appropriate API functions based on the type
  const getPreferences = type === 'tag' 
    ? getUserTagPreferences 
    : getUserToolPreferences
  
  const updatePreferenceFunc = type === 'tag' 
    ? updateUserTagPreference 
    : updateUserToolPreference
  
  const deletePreferenceFunc = type === 'tag' 
    ? deleteUserTagPreference 
    : deleteUserToolPreference
  
  // Generic query to fetch preferences
  const { data: preferences, isLoading } = useQuery<Preferences>({
    queryKey,
    queryFn: async () => {
      return getPreferences(userId) || { included: [], excluded: [] }
    },
  })
  
  // Generic mutation to update preferences
  const updatePreference = useMutation({
    mutationFn: async ({ 
      itemName, 
      preferenceType 
    }: { 
      itemName: string
      preferenceType: PreferenceType<T> | null 
    }) => {
      if (preferenceType === null) {
        console.log(`Deleting preference for ${type}:`, itemName)
        await deletePreferenceFunc(userId, itemName)
      } else {
        console.log(`Updating preference for ${type}:`, itemName, 'to:', preferenceType)
        await updatePreferenceFunc(userId, itemName, preferenceType as any)
      }
    },
    onMutate: async ({ itemName, preferenceType }) => {
      await queryClient.cancelQueries({ queryKey })

      const previousPreferences = queryClient.getQueryData<Preferences>(queryKey)
      if (!previousPreferences) return { previousPreferences }
      // Logic for multiple tags
      const newPreferences = {
        included: [],
        excluded: [],
      }

      if (preferenceType === 'included') {
        newPreferences.excluded = newPreferences.excluded.filter(t => t !== itemName)
        const index = newPreferences.included.indexOf(itemName)
        if (index === -1) {
          newPreferences.included.push(itemName)
        } else {
          newPreferences.included.splice(index, 1)
        }
      } else if (preferenceType === 'excluded') {
        newPreferences.included = newPreferences.included.filter(t => t !== itemName)
        const index = newPreferences.excluded.indexOf(itemName)
        if (index === -1) {
          newPreferences.excluded.push(itemName)
        } else {
          newPreferences.excluded.splice(index, 1)
        }
      } else if (preferenceType === null) {
        // Remove from both lists to return to default state
        newPreferences.included = newPreferences.included.filter(t => t !== itemName)
        newPreferences.excluded = newPreferences.excluded.filter(t => t !== itemName)
      }

      queryClient.setQueryData(queryKey, newPreferences)

      return { previousPreferences }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousPreferences) {
        queryClient.setQueryData(queryKey, context.previousPreferences)
      }
    },
    // onSettled: () => {
    //   queryClient.invalidateQueries({ queryKey })
    // },
  })

  return {
    preferences: preferences || { included: [], excluded: [] },
    isLoading,
    updatePreference,
  }
}
